/**
 * The Serials cell of a Rebate Report line (flag `serial_capture`, MOT-357):
 * how many of the claimed units carry a serial, and, for an owner, admin or
 * manager, a dialog that writes serials onto each sold line behind the report
 * line (`PUT /store/serials/order-items/:id`), so the claim export carries
 * them. The serial attaches to the sale line, never to the product.
 *
 * Slice 2 (MOT-374): each serial's rebate claim state (not claimed /
 * submitted / paid) is shown on the chip and set per serial in the dialog
 * (`POST /store/serials/claims`), applied at once like a status toggle. The
 * state is this report line's own (MOT-420): it is kept under the sold item's
 * `programKey`, so a unit that earns a bundle and a sheet rebate shows each
 * claim on its own line.
 */
import React, { useMemo, useState } from 'react';
import { Box, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { makeApiCall, getAuthHeaders } from '../../utils/apiConfig';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';
import { FormDialog, StatusChip } from '../ui';
import { tokens } from '../../theme/tokens';

async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({}));
  return body?.error?.message || body?.error || body?.message || fallback;
}

export type SerialClaimStatus = 'submitted' | 'paid';

export interface RebateReportSoldItem {
  orderItemId: string;
  orderNumber: string;
  orderedAt: string | null;
  quantity: number;
  serialNumbers: string[];
  /** The report line's rebate program the claims below are kept under; sent back with each claim. */
  programKey: string;
  /** Claim state of the serials that have one under `programKey`; a serial not listed is not claimed. */
  serialClaims?: Array<{ serial: string; status: SerialClaimStatus; reference: string | null }>;
}

/** `POST /store/serials/claims`: shared by this cell and the report's "Mark serials submitted". */
export const postSerialClaims = async (
  body: {
    status: SerialClaimStatus | 'unclaimed';
    reference?: string | null;
    items: Array<{ orderItemId: string; programKey: string; serials: string[] }>;
  },
  fallbackError: string,
): Promise<{ updated: number; unchanged: number; skipped: Array<{ orderItemId: string; programKey: string; serial: string; reason: string }> }> => {
  const headers = await getAuthHeaders();
  const res = await makeApiCall('/store/serials/claims', { method: 'POST', headers, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(await readError(res, fallbackError));
  return (await res.json()).data;
};

interface Props {
  productName: string;
  serialNumbers: string[];
  soldItems: RebateReportSoldItem[];
  unitsSold: number;
  canEdit: boolean;
  onSaved: () => void;
}

export const RebateLineSerialsCell: React.FC<Props> = ({ productName, serialNumbers, soldItems, unitsSold, canEdit, onSaved }) => {
  const { t } = useAppTranslation();
  const { formatDate } = useLocale();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const withSerial = useMemo(
    () => soldItems.reduce((sum, s) => sum + Math.min(s.serialNumbers.length, s.quantity), 0),
    [soldItems],
  );
  const complete = unitsSold > 0 && withSerial >= unitsSold;
  const claimCounts = useMemo(() => {
    const claims = soldItems.flatMap((s) => s.serialClaims ?? []);
    return { submitted: claims.filter((c) => c.status === 'submitted').length, paid: claims.filter((c) => c.status === 'paid').length };
  }, [soldItems]);
  const claimOf = (item: RebateReportSoldItem, serial: string): SerialClaimStatus | 'unclaimed' =>
    item.serialClaims?.find((c) => c.serial === serial)?.status ?? 'unclaimed';

  const claimMutation = useMutation({
    mutationFn: async ({ orderItemId, programKey, serial, status }: {
      orderItemId: string;
      programKey: string;
      serial: string;
      status: SerialClaimStatus | 'unclaimed';
    }) => {
      await postSerialClaims({ status, items: [{ orderItemId, programKey, serials: [serial] }] }, t('store.rebateSerials.claim.error'));
      return serial;
    },
    onSuccess: (serial) => {
      onSaved();
      showToast(t('store.rebateSerials.claim.saved', { serial }), 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const openDialog = () => {
    setDrafts(Object.fromEntries(soldItems.map((s) => [s.orderItemId, s.serialNumbers.join(', ')])));
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const changed = soldItems.filter((s) => (drafts[s.orderItemId] ?? '') !== s.serialNumbers.join(', '));
      const failures: string[] = [];
      for (const item of changed) {
        const serialNumbers = (drafts[item.orderItemId] ?? '')
          .split(/[,;|\s]+/)
          .map((v) => v.trim())
          .filter(Boolean);
        const res = await makeApiCall(`/store/serials/order-items/${encodeURIComponent(item.orderItemId)}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ serialNumbers }),
        });
        if (!res.ok) failures.push(`${item.orderNumber}: ${await readError(res, t('store.rebateSerials.saveError'))}`);
      }
      return { saved: changed.length - failures.length, failures };
    },
    onSuccess: ({ saved, failures }) => {
      onSaved();
      if (failures.length === 0) {
        setOpen(false);
        showToast(t('store.rebateSerials.saved', { count: saved }), 'success');
      } else {
        // Partial failure: say exactly which orders did not take, and keep the dialog open.
        showToast(failures.join(' · '), 'error');
      }
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const summary = soldItems.length === 0
    ? '—'
    : t('store.rebateSerials.count', { withSerial, units: unitsSold });

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
        <Tooltip
          title={
            serialNumbers.length > 0
              ? serialNumbers.slice(0, 25).join(', ') + (serialNumbers.length > 25 ? '…' : '')
                + (claimCounts.submitted + claimCounts.paid > 0 ? ` · ${t('store.rebateSerials.claim.summary', claimCounts)}` : '')
              : t('store.rebateSerials.none')
          }
        >
          <span>
            <StatusChip
              size="small"
              tone={soldItems.length === 0 ? 'neutral' : complete ? 'positive' : 'caution'}
              label={summary}
              dot={soldItems.length > 0}
            />
          </span>
        </Tooltip>
        {canEdit && soldItems.length > 0 && (
          <Tooltip title={t('store.rebateSerials.edit')}>
            <IconButton size="small" onClick={openDialog} aria-label={t('store.rebateSerials.edit')}>
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <FormDialog
        open={open}
        title={t('store.rebateSerials.dialogTitle', { product: productName })}
        onClose={() => setOpen(false)}
        onSubmit={() => save.mutate()}
        submitLabel={t('store.rebateSerials.save')}
        cancelLabel={t('common.cancel')}
        loading={save.isPending}
        maxWidth="sm"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">{t('store.rebateSerials.dialogHelp')}</Typography>
          {soldItems.map((item) => (
            <Box key={item.orderItemId}>
              <Typography variant="caption" sx={{ color: tokens.ink.faint }}>
                {t('store.rebateSerials.lineLabel', {
                  order: item.orderNumber,
                  date: item.orderedAt ? formatDate(item.orderedAt) : '',
                  quantity: item.quantity,
                })}
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={drafts[item.orderItemId] ?? ''}
                onChange={(e) => setDrafts((d) => ({ ...d, [item.orderItemId]: e.target.value }))}
                placeholder={t('store.rebateSerials.placeholder', { quantity: item.quantity })}
                inputProps={{ spellCheck: false, style: { fontFamily: tokens.font.mono } }}
              />
              {item.serialNumbers.length > 0 && (
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {item.serialNumbers.map((serial) => (
                    <Stack key={serial} direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontFamily: tokens.font.mono, flex: 1, minWidth: 0 }} noWrap>{serial}</Typography>
                      <TextField
                        select
                        size="small"
                        label={t('store.rebateSerials.claim.label')}
                        value={claimOf(item, serial)}
                        disabled={claimMutation.isPending}
                        onChange={(e) => claimMutation.mutate({
                          orderItemId: item.orderItemId,
                          programKey: item.programKey,
                          serial,
                          status: e.target.value as SerialClaimStatus | 'unclaimed',
                        })}
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="unclaimed">{t('store.rebateSerials.claim.unclaimed')}</MenuItem>
                        <MenuItem value="submitted">{t('store.rebateSerials.claim.submitted')}</MenuItem>
                        <MenuItem value="paid">{t('store.rebateSerials.claim.paid')}</MenuItem>
                      </TextField>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
          {soldItems.some((item) => item.serialNumbers.length > 0) && (
            <Typography variant="caption" color="text.secondary">{t('store.rebateSerials.claim.help')}</Typography>
          )}
        </Stack>
      </FormDialog>
    </>
  );
};
